import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Dropdown } from 'primereact/dropdown';
import InfoCard from '../InfoCard/InfoCard';
import DataModal from '../DataModal/DataModal';
import { SectionType } from '../DataModal/DataModal.interface';
import ContentSectionSkeleton from '../SectionSkeletons/ContentSectionSkeleton';
import LossOfExclusivityDetailModal from './components/LossOfExclusivityDetailModal/LossOfExclusivityDetailModal';
import SolicitationDetailModal, {
  SolicitationDetailsResponse,
} from '../SolicitationDetailModal/SolicitationDetailModal';
import { ANDADetailModal } from 'components/shared/ANDADetailModal';
import { InfoCardData, ANDAInfoCardData } from 'Interfaces/ProductWikiInterfaces';
import { LossOfExclusivityInfoCardData } from 'Interfaces/ProductWikiInterfaces/landingPage.types';
import { getColumnsBySectionType } from '../DataModal/modalColumnConfigs';
import { useStore } from 'hooks/useStore';
import { getSolicitationDetails } from 'services/productWikiServices';
import './LossOfExclusivitySection.styles.scss';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';

interface LossOfExclusivitySectionProps {
  lossOfExclusivity: LossOfExclusivityInfoCardData[];
}

interface PeriodOption {
  label: string;
  value: number;
}

const LossOfExclusivitySection: React.FC<LossOfExclusivitySectionProps> = observer(
  ({ lossOfExclusivity }) => {
    const [showModal, setShowModal] = useState<boolean>(false);
    const [currentSection, setCurrentSection] = useState<SectionType>('lossOfExclusivity');
    const [showDetailModal, setShowDetailModal] = useState<boolean>(false);
    const [selectedCard, setSelectedCard] = useState<LossOfExclusivityInfoCardData | null>(null);
    // ANDA detail modal state
    const [showAndaDetailModal, setShowAndaDetailModal] = useState<boolean>(false);
    const [selectedAnda, setSelectedAnda] = useState<ANDAInfoCardData | null>(null);
    // Solicitation detail modal state
    const [showSolicitationDetailModal, setShowSolicitationDetailModal] = useState<boolean>(false);
    const [solicitationDetails, setSolicitationDetails] =
      useState<SolicitationDetailsResponse | null>(null);
    const [isSolicitationDetailsLoading, setIsSolicitationDetailsLoading] =
      useState<boolean>(false);

    const {
      LandingPageStore: {
        fetchContractExpirations,
        fetchAndaUpdates,
        fetchLossOfExclusivity,
        isContractExpirationsLoading,
        isAndaUpdatesLoading,
        isLossOfExclusivityLoading,
        isPreSolicitationAlertsLoading,
        landingPageData,
        selectedContractExpirationsPeriod,
        selectedAndaUpdatesPeriod,
        selectedLossOfExclusivityPeriod,
      },
    } = useStore();

    const periodOptions: PeriodOption[] = [
      { label: 'Next 3 months', value: 90 },
      { label: 'Next 6 months', value: 180 },
      { label: 'Next 12 months', value: 365 },
    ];

    const andaPeriodOptions: PeriodOption[] = [
      { label: 'Past 3 months', value: 90 },
      { label: 'Past 6 months', value: 180 },
      { label: 'Past 12 months', value: 365 },
    ];

    const handlePeriodChange = async (e: { value: number | string }) => {
      const periodValue = typeof e.value === 'number' ? e.value : parseInt(e.value as string, 10);
      // Fetch Loss of Exclusivity with the selected period (store will update selectedLossOfExclusivityPeriod)
      await fetchLossOfExclusivity(periodValue);
    };

    const handleContractPeriodChange = async (e: { value: number | string }) => {
      const periodValue = typeof e.value === 'number' ? e.value : parseInt(e.value as string, 10);
      await fetchContractExpirations(periodValue);
    };

    const handleAndaPeriodChange = async (e: { value: number | string }) => {
      const periodValue = typeof e.value === 'number' ? e.value : parseInt(e.value as string, 10);
      await fetchAndaUpdates(periodValue);
    };

    // Get loading state for current section
    const getCurrentSectionLoading = (): boolean => {
      switch (currentSection) {
        case 'contractExpirations':
          return isContractExpirationsLoading;
        case 'andaUpdates':
          return isAndaUpdatesLoading;
        case 'lossOfExclusivity':
          return isLossOfExclusivityLoading;
        case 'preSolicitationAlerts':
          return isPreSolicitationAlertsLoading;
        default:
          return false;
      }
    };

    const displayedItems = lossOfExclusivity.slice(0, 6);
    const hasMoreItems = lossOfExclusivity.length > 6;

    // Truncate description to 30 characters
    const truncateDescription = (description: string, maxLength: number = 50): string => {
      if (!description || description.length <= maxLength) return description;
      return description.substring(0, maxLength) + '...';
    };

    // Handle card click to open detail modal
    const handleCardClick = (item: LossOfExclusivityInfoCardData) => {
      setSelectedCard(item);
      setShowDetailModal(true);
    };

    // Handle ANDA card click to open detail modal (for when user switches to ANDA section)
    const handleAndaCardClick = (item: ANDAInfoCardData) => {
      setSelectedAnda(item);
      setShowAndaDetailModal(true);
    };

    // Handle Solicitation card click to fetch details and open modal
    const handleSolicitationCardClick = async (item: InfoCardData) => {
      const solicitationNumber = item.solicitation_number;
      if (!solicitationNumber) {
        console.error('No solicitation number available for this item');
        return;
      }

      setIsSolicitationDetailsLoading(true);
      setShowSolicitationDetailModal(true);

      try {
        const response = await getSolicitationDetails(solicitationNumber);
        setSolicitationDetails(response.data);
      } catch (error) {
        console.error('Error fetching solicitation details:', error);
        setSolicitationDetails(null);
      } finally {
        setIsSolicitationDetailsLoading(false);
      }
    };

    // Handle row click based on current section
    const handleRowClick = (row: InfoCardData | ANDAInfoCardData | LossOfExclusivityInfoCardData) => {
      if (currentSection === 'andaUpdates') {
        handleAndaCardClick(row as ANDAInfoCardData);
      } else if (currentSection === 'lossOfExclusivity') {
        handleCardClick(row as LossOfExclusivityInfoCardData);
      } else if (currentSection === 'preSolicitationAlerts') {
        handleSolicitationCardClick(row as InfoCardData);
      }
    };

    // Get data for current section
    const getSectionData = (section: SectionType): (InfoCardData | ANDAInfoCardData | LossOfExclusivityInfoCardData)[] => {
      if (!landingPageData) return [];

      switch (section) {
        case 'contractExpirations':
          return landingPageData.contractExpirations || [];
        case 'andaUpdates':
          return landingPageData.andaUpdates || [];
        case 'preSolicitationAlerts':
          return landingPageData.preSolicitationAlerts || [];
        case 'lossOfExclusivity':
          return landingPageData.lossOfExclusivity || [];
        // case 'marketIntelligence':
        //   return landingPageData.marketIntelligence || [];
        default:
          return [];
      }
    };

    // Handle section change in modal
    const handleSectionChange = (section: SectionType) => {
      setCurrentSection(section);
    };

    // Get columns for current section
    let columns = getColumnsBySectionType(currentSection);
    const modalData = getSectionData(currentSection);

    // Override description column for lossOfExclusivity to add truncation and "read more"
    if (currentSection === 'lossOfExclusivity') {
      columns = columns.map(column => {
        if (column.field === 'description') {
          return {
            ...column,
            render: (value: string, _row: LossOfExclusivityInfoCardData) => {
              const maxLength = 50;
              // Ensure value is a string
              const stringValue = value ? String(value) : '';

              if (!stringValue) {
                return React.createElement('span', null, '-');
              }

              // Check if truncation is needed
              if (stringValue.length > maxLength) {
                const truncated = stringValue.substring(0, maxLength) + '...';
                return React.createElement('span', null, truncated);
              }

              // Return full value if it's short enough
              return React.createElement('span', null, stringValue);
            },
          };
        }
        return column;
      });
    }

    return (
      <>
        <section className="content-section">
          <div className="section-header">
            <div className="section-title">
              <i className="fas fa-exclamation-circle" style={{ color: 'var(--error-color)' }}></i>
              Loss of Exclusivity
              <span className="section-badge">{lossOfExclusivity.length} Upcoming</span>
            </div>
            <div className="section-header-right">
              <div className="period-selector">
                <Dropdown
                  value={selectedLossOfExclusivityPeriod}
                  options={periodOptions}
                  onChange={handlePeriodChange}
                  placeholder="Select Period"
                  className="period-dropdown"
                  appendTo={typeof document !== 'undefined' ? document.body : undefined}
                />
              </div>
            </div>
          </div>
          <div className="section-content">
            {isLossOfExclusivityLoading ? (
              <ContentSectionSkeleton />
            ) : lossOfExclusivity.length === 0 ? (
              <div className="no-data-message">No data available for Loss of Exclusivity</div>
            ) : (
              <>
                <div className="three-card-grid">
                  {displayedItems.map((item, index) => (
                    <div key={index} className="three-card-wrapper">
                      <InfoCard
                        {...item}
                        sectionType="lossOfExclusivity"
                        description={truncateDescription(item.description)}
                        onCardClick={() => handleCardClick(item)}
                      />
                    </div>
                  ))}
                </div>
                {hasMoreItems && (
                  <div className="see-more-container">
                    <button className="see-more-btn" onClick={() => setShowModal(true)}>
                      See More
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        <DataModal
          visible={showModal}
          onHide={() => {
            setShowModal(false);
            setCurrentSection('lossOfExclusivity'); // Reset to default section when closing
          }}
          data={modalData}
          sectionType={currentSection}
          columns={columns}
          onSectionChange={handleSectionChange}
          sectionDropdownConfigs={{
            contractExpirations: {
              value: selectedContractExpirationsPeriod,
              options: periodOptions,
              onChange: handleContractPeriodChange,
              placeholder: 'Select Period',
            },
            andaUpdates: {
              value: selectedAndaUpdatesPeriod,
              options: andaPeriodOptions,
              onChange: handleAndaPeriodChange,
              placeholder: 'Select Period',
            },
            lossOfExclusivity: {
              value: selectedLossOfExclusivityPeriod,
              options: periodOptions,
              onChange: handlePeriodChange,
              placeholder: 'Select Period',
            },
          }}
          isLoading={getCurrentSectionLoading()}
          onRowClick={
            currentSection === 'andaUpdates' ||
            currentSection === 'lossOfExclusivity' ||
            currentSection === 'preSolicitationAlerts'
              ? handleRowClick
              : undefined
          }
        />

        <LossOfExclusivityDetailModal
          visible={showDetailModal}
          onHide={() => {
            setShowDetailModal(false);
            setSelectedCard(null);
          }}
          data={selectedCard}
        />

        {/* ANDA Detail Modal - for when user switches to ANDA section */}
        <ANDADetailModal
          visible={showAndaDetailModal}
          onHide={() => {
            setShowAndaDetailModal(false);
            setSelectedAnda(null);
          }}
          data={selectedAnda}
        />

        {/* Solicitation Detail Modal - for when user switches to Solicitation Alerts section */}
        <SolicitationDetailModal
          visible={showSolicitationDetailModal}
          onHide={() => {
            setShowSolicitationDetailModal(false);
            setSolicitationDetails(null);
          }}
          data={solicitationDetails}
          isLoading={isSolicitationDetailsLoading}
        />
      </>
    );
  }
);

export default LossOfExclusivitySection;
